import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './api.js';
import { createLatestGate } from './searchAsYouType.js';

export interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
  /**
   * ehr#86 - fetch again in the background: no loading state, and a failed refresh keeps
   * the data already on screen (a blip in a live view shouldn't blank it).
   */
  refresh: () => void;
}

/** Small data-fetch hook with loading / error / reload — the states each tab renders. */
export function useFetch<T>(fetcher: () => Promise<T>, deps: unknown[] = []): FetchState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const mounted = useRef(true);
  // ehr#777 - a background refresh started before the deps changed (say the search box
  // moved on) must not land its older answer over the newer one.
  const gateRef = useRef(createLatestGate());
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const refresh = useCallback(() => {
    const token = gateRef.current.peek();
    fetcherRef
      .current()
      .then((d) => {
        if (mounted.current && gateRef.current.isCurrent(token)) setData(d);
      })
      .catch(() => {
        /* keep what's shown; the next refresh or a reload will try again */
      });
  }, []);

  useEffect(() => {
    let alive = true;
    gateRef.current.next();
    setLoading(true);
    setError(null);
    fetcher()
      .then((d) => {
        if (alive) setData(d);
      })
      .catch((e: unknown) => {
        if (alive) setError(e instanceof ApiError ? e.message : String(e));
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [...deps, nonce]);

  return { data, loading, error, reload, refresh };
}
